<svelte:options
	customElement={{
		tag: 'vv-directory'
	}}
/>

<script context="module">
	import appCssUrl from '../../app.css?inline';
	let style = '<style' + `>${appCssUrl}</style>`;
</script>

<script lang="ts">
	import Button from '../../lib/Button.svelte';
	import AddressCollectionEditBox from '../../lib/AddressCollectionEditBox.svelte';
	import { type Address, addressStore, getResolvedAddresses, addressNeedsGeocoding } from '../../api/address.ts';
	import {
		countryStrings,
		getApplicationSettings
	} from '../../api/common.ts';

	import {
		type PoliticianIdentifier,
		type Category,
		directoryStore,
	} from '../../api/officials.ts';

	import { getProfiles } from '../../api/elections.ts';
	import * as t from '../../translations/Directory.ts';
	import { tryLoadUser, userStore } from '../../api/users.ts';
	
	import OfficialsPage from './OfficialsPage.svelte';
	import ProfilePage from './ProfilePage.svelte';
	import ComposeMessagePage from './ComposeMessagePage.svelte';
	import MessageConfirmationPage from './MessageConfirmationPage.svelte';

	import Router from 'svelte-spa-router'
	import { push, replace} from 'svelte-spa-router'
	import { wrap } from 'svelte-spa-router/wrap'

	const applicationType = 'advocacy';
	let pageElement: Element;
	let selectedAddress: Address;
	let selectedAddressType: string;
	let lockAddress = false;
	let currPrevCountry: string = '';
	let applicationSettings : ApplicationSettings; 
	let addressTypes;
	let prevRoute;
	let currRoute;
	let isAddressLoading = true;
	
	const routes = {
		'/Officials': wrap({
			component: OfficialsPage,
			conditions: [
				async (detail) => {
					return await checkAddressPreCondition();
				}
			]
		}),
		'/Officials/:id': wrap({
			component: ProfilePage,
			conditions: [
				async (detail) => {
					return await checkAddressPreCondition();
				},
				async (detail) => {
					return await checkOfficialProfilePreCondition(detail?.params?.id);
				}
			],
			props: {
				getPreviousRoute: function () {
					return prevRoute;
				}
			}
		}),
		'/Officials/:id/Message': wrap({
			component: ComposeMessagePage,
			conditions: [
				async (detail) => {
					return await checkAddressPreCondition();
				},
				async (detail) => {
					return await checkOfficialProfilePreCondition(detail?.params?.id);
				},
				async (detail) => {
					return await checkRememberedUserPrecondition();
				},
				// omit check for matched targets and hard code type E for target type with media removed from directory
			],
			props: {
				getPreviousRoute: function () {
					return prevRoute;
				}
			},
			userData: {
				routeName: 'Message'
			}
		}),
		'/Officials/:id/MessageConfirmation': wrap({
			component: MessageConfirmationPage,
			conditions: [
				async (detail) => {
					return await checkAddressPreCondition();
				},
				async (detail) => {
					return await checkOfficialProfilePreCondition(detail?.params?.id);
				}
			]
		}),
	}
	
	async function checkAddressPreCondition() {
		$userStore.user = await tryLoadUser();
		applicationSettings = await getApplicationSettings(applicationType);
		
		const visibleAddressTypes = [];
		if (applicationSettings.userProfile.homeAddressRequired || 
			applicationSettings.userProfile.homeAddressVisible && 
			applicationSettings.userProfile.businessAddressRequired && $userStore.user?.businessAddress?.zipCode !== undefined && $userStore.user?.businessAddress?.zipCode !== undefined) {
			visibleAddressTypes.push('H');
		}

		if (applicationSettings.userProfile.businessAddressRequired ||
			applicationSettings.userProfile.businessAddressVisible &&
			applicationSettings.userProfile.homeAddressRequired && $userStore.user?.homeAddress?.zipCode !== undefined && $userStore.user?.homeAddress?.zipCode !== undefined) {
			visibleAddressTypes.push('B');
		}
		addressTypes = visibleAddressTypes;
		
		if (!$addressStore.selectedAddress?.checksum) {
			const searchParams = new URLSearchParams(document.location.search);
			const addressCollection = await getResolvedAddresses(searchParams, $userStore.user, applicationType);
			selectedAddress = addressCollection.selectedAddress;
			currPrevCountry = selectedAddress?.country!.toLowerCase();

			if (visibleAddressTypes.find(addressType => addressType === selectedAddress?.addressType) !== undefined) {
				$addressStore.addresses = addressCollection.addresses;
				$addressStore.selectedAddress = selectedAddress;
			} else {
				$addressStore.addresses = addressCollection.addresses;
				if (visibleAddressTypes.length > 0) {
					$addressStore.selectedAddress = $addressStore.addresses.get(visibleAddressTypes[0]);	
				}
			}
		}

		isAddressLoading = false;
		
		return !addressNeedsGeocoding($addressStore.selectedAddress);
	}
	
	async function checkOfficialProfilePreCondition(officialId) {
		const profile = $directoryStore.categories
				.map(category => (category ? category : []).profiles.map(profiles => profiles))
				.flat()
				.find(p => p.id == officialId);
		
		
		if (!profile) {
			let category : Category = $directoryStore.categories.find(i => i.referenceName === null);
			if (!category) {
				category = { referenceName: null, profiles:[] };
				$directoryStore.categories.push(category)
			}
			
			let profilesToGet: PoliticianIdentifier[] = [];
			profilesToGet.push({ id: officialId, type: 'E' });
			
			category.profiles = await getProfiles(profilesToGet);
			$directoryStore.key = '';
		}
		
		return $directoryStore.categories
				.map(category => (category ? category : []).profiles.map(profiles => profiles))
				.flat()
				.find(p => p.id == officialId);
	}
	
	async function checkRememberedUserPrecondition(){
		if (!($userStore.user?.userId > 0)) {
			$userStore.user = await tryLoadUser();
		}

		return true;
	}
	
	/** Official page (profile or message) the visitor asked for before being sent to enter an address. */
	let routeAwaitingAddress: string | null = null;

	function selectAddress() {
		if (selectedAddress?.checksum) {
			const next = routeAwaitingAddress ?? '/Officials';
			routeAwaitingAddress = null;
			push(next)
		}
	}
	
	function onClearAddress(){
		routeAwaitingAddress = null;
		push('/')
	}
	
	let editingAddress = !selectedAddress?.checksum;

	function routeLoading(event) {
		editingAddress = event.detail.route === '/';
		lockAddress = event.detail.userData?.routeName === 'Message'
	}

	function routeLoaded(event) {
	
		
		prevRoute = currRoute;
		currRoute = event.detail.route;
	}

	function conditionsFailed(event) {
		const location: string | undefined = event.detail?.location;
		const address = $addressStore.selectedAddress;
		routeAwaitingAddress =
			location?.startsWith('/Officials/') && (!address || addressNeedsGeocoding(address))
				? location
				: null;
		replace('/')
	}

	async function load() {
		checkAddressPreCondition();
	}
	
	load();
</script>

{@html style}
<div
	id="directory-page"
	bind:this={pageElement}
	class="@container w-full m-auto max-w-[1000px] p-2 flex flex-col gap-2"
>
		<div class="flex flex-col @xl:flex-row gap-4">
			<div class="flex-1">
				<div class="flex flex-row">
					<div>
						<h1 class="text-4xl">{t.Page_Header()}</h1>
					</div>
				</div>
				{#if currPrevCountry !== countryStrings.ca}
					<div
						class="border-l-4 border-solid border-[var(--vv-action-center-button-color)] px-4 pt-1 flex-1 flex flex-col grow-0 my-4"
					>
						<div class="text-lg font-bold">{t.Visit_ElectionCenter_Header()}</div>
						<div>{t.Visit_ElectionCenter_SubHeader()}</div>
						<div class="flex-1"></div>
						<div>
							<Button
								tag="a"
								type="action-center"
								href="./ElectionCenter/"
								class="inline-flex flex-row w-40 mt-4 justify-center border"
							>
								{t.Visit_ElectionCenter_ButtonText()}
							</Button>
						</div>
					</div>
				{/if}
			</div>
			
			<div class="flex-1 flex flex-col">
				<AddressCollectionEditBox
					bind:editingAddress
					bind:selectedAddress
					onSelectAddress={selectAddress}
					onClearAddress={onClearAddress}
					lockAddress={lockAddress}
					addressTypes={addressTypes}
					isLoading={isAddressLoading}
				>
					{t.EmptyAddress_Header()}
				</AddressCollectionEditBox>
			</div>
		</div>

	<div class="flex flex-col grid-cols-6 @xl:grid gap-4">
		<Router {routes} 
				on:routeLoading={routeLoading}
				on:routeLoaded={routeLoaded}
				on:conditionsFailed={conditionsFailed}
		/>
	</div>
</div>
